// ── Counter Intelligence: report query layer ─────────────────────────────────
// The "tool layer" between a plain-English report question ("what was my
// inventory value for August?", "compare August and September COGS") and the
// report generators in ciReports.mjs.
//
// It has three jobs, all pure or dependency-injected so they are unit-testable:
//   1. Intent mapping — parseReportQuestion() turns a question into
//      { report_type, ranges, scope, vendorScope, comparison, … } without a
//      model call (the server classifier is the fallback for phrasings this
//      misses; it returns route='report_query' + report_type).
//   2. Session lookup — listReportSessionsForAI() / findCompletedCountsForAI()
//      find the completed count sessions a report can be built from, and
//      listEopReportsForAI() finds saved End of Period (pnl_reports) rows.
//      Reports and completed counts are the SOURCE OF TRUTH: Counter
//      Intelligence never derives official totals any other way.
//   3. Disambiguation — chips + reply resolvers for "which count / which
//      report / dollar value or quantities", and prepareReportRunForAI() for
//      the "no matching report yet — here's how to run it" next step.
//
// Session eligibility mirrors the Reports page (sessionLifecycle.isReportable):
// completed, not voided, not trashed, not a template. Archived counts stay IN
// reports, exactly like the Reports page.

// ── Report types ──────────────────────────────────────────────────────────────
export const REPORT_QUERY_TYPES = [
  'inventory_value',
  'inventory_totals',
  'purchase_history',
  'eop_cogs',
  'count_review',
  'price_changes',
];

export const REPORT_TYPE_LABELS = {
  inventory_value: 'Inventory Value',
  inventory_totals: 'Inventory Totals',
  purchase_history: 'Purchase History',
  eop_cogs: 'End of Period / Actual COGS',
  count_review: 'Count Review',
  price_changes: 'Price Changes',
};

// ── Date helpers ──────────────────────────────────────────────────────────────
const isoDay = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
// Accepted spellings per month (index-aligned with MONTH_NAMES).
const MONTH_SPELLINGS = [
  ['january', 'jan'], ['february', 'feb'], ['march', 'mar'], ['april', 'apr'],
  ['may'], ['june', 'jun'], ['july', 'jul'], ['august', 'aug'],
  ['september', 'sept', 'sep'], ['october', 'oct'], ['november', 'nov'], ['december', 'dec'],
];

const monthRange = (year, monthIdx, { assumed = false } = {}) => {
  const lastDay = new Date(year, monthIdx + 1, 0).getDate();
  const mm = String(monthIdx + 1).padStart(2, '0');
  return {
    kind: 'month',
    startDate: `${year}-${mm}-01`,
    endDate: `${year}-${mm}-${String(lastDay).padStart(2, '0')}`,
    label: `${MONTH_NAMES[monthIdx]} ${year}`,
    assumed,
  };
};

const dayRange = (date, label) => ({
  kind: 'day', startDate: isoDay(date), endDate: isoDay(date), label, assumed: false,
});

// Small counts written as words ("the past two weeks", "a couple of days").
const WORD_COUNTS = {
  one: 1, two: 2, three: 3, four: 4, five: 5,
  six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
  couple: 2, few: 3,
};
const COUNT_WORD_PATTERN = '\\d{1,3}|one|two|three|four|five|six|seven|eight|nine|ten|couple(?:\\s+of)?|few';
const parseCountWord = (raw) => {
  const t = String(raw || '').toLowerCase().replace(/\s+of$/, '').trim();
  const n = /^\d+$/.test(t) ? parseInt(t, 10) : WORD_COUNTS[t];
  return Number.isInteger(n) && n >= 1 && n <= 366 ? n : null;
};

// The n calendar days ending today: "the past 3 days" = the day before
// yesterday through today, inclusive.
const trailingDaysRange = (now, n, label) => ({
  kind: 'days',
  startDate: isoDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() - (n - 1))),
  endDate: isoDay(now),
  label,
  assumed: false,
});

const trailingMonthsRange = (now, n, label) => ({
  kind: 'days',
  startDate: isoDay(new Date(now.getFullYear(), now.getMonth() - n, now.getDate())),
  endDate: isoDay(now),
  label,
  assumed: false,
});

/**
 * Extract up to two date ranges from free text, in order of appearance.
 * Handles specific days ("May 31st", "Sept. 5, 2025") and whole months.
 * Mentions without a year resolve to the most recent occurrence that has
 * already happened/started (in July 2026, "August" → August 2025, "May 31st"
 * → May 31, 2026) and are flagged `assumed` so the answer can state it.
 */
export const parseDateRanges = (raw, now = new Date()) => {
  const text = String(raw || '').toLowerCase();
  if (!text) return [];
  const found = [];
  // Text spans already claimed by a month+day date, so the bare-month pass
  // doesn't double-report the same mention as a whole-month range.
  const consumed = [];

  // Specific days first: "may 31st", "june 1", "sept. 5, 2025".
  MONTH_SPELLINGS.forEach((spellings, monthIdx) => {
    for (const name of spellings) {
      const re = new RegExp(`\\b${name}\\b\\.?\\s+(\\d{1,2})(?:st|nd|rd|th)?\\b(?:,?\\s*((?:19|20)\\d{2}))?`, 'g');
      let m;
      while ((m = re.exec(text)) !== null) {
        const day = parseInt(m[1], 10);
        if (!(day >= 1 && day <= 31)) continue;
        let year;
        let assumed = false;
        if (m[2]) {
          year = parseInt(m[2], 10);
        } else {
          year = now.getFullYear();
          if (new Date(year, monthIdx, day) > now) year -= 1;
          assumed = true;
        }
        const d = new Date(year, monthIdx, day);
        if (d.getMonth() !== monthIdx) continue; // e.g. "February 30th"
        found.push({
          index: m.index,
          range: { kind: 'day', startDate: isoDay(d), endDate: isoDay(d), label: `${MONTH_NAMES[monthIdx]} ${day}, ${year}`, assumed },
        });
        consumed.push([m.index, m.index + m[0].length]);
      }
    }
  });

  MONTH_SPELLINGS.forEach((spellings, monthIdx) => {
    for (const name of spellings) {
      // `\.?` lets "Sept. 2024" keep its year; distinct spellings can't both
      // match the same occurrence (word boundaries), so no cross-spelling dupes.
      const re = new RegExp(`\\b${name}\\b\\.?(?:\\s+of)?[\\s,]*((?:19|20)\\d{2})?`, 'g');
      let m;
      while ((m = re.exec(text)) !== null) {
        if (consumed.some(([s, e]) => m.index >= s && m.index < e)) continue;
        // "may" is usually a verb — only treat it as a month with a year, or
        // right after a preposition/comparison word ("in May", "compare May…").
        if (name === 'may' && !m[1]) {
          const before = text.slice(0, m.index).trimEnd();
          if (!/(?:\b(?:in|for|of|during|since|between|and|to|vs|versus|compare|last|this|early|late|mid)|,)$/.test(before)) continue;
        }
        let year;
        let assumed = false;
        if (m[1]) {
          year = parseInt(m[1], 10);
        } else {
          // Most recent occurrence that has already started.
          year = monthIdx > now.getMonth() ? now.getFullYear() - 1 : now.getFullYear();
          assumed = true;
        }
        // Keep EVERY mention — "compare August 2024 and August 2025" needs both.
        found.push({ index: m.index, range: monthRange(year, monthIdx, { assumed }) });
      }
    }
  });

  const relative = [
    [/\blast month\b/, () => monthRange(
      now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear(),
      now.getMonth() === 0 ? 11 : now.getMonth() - 1,
    )],
    [/\bthis month\b/, () => monthRange(now.getFullYear(), now.getMonth())],
    [/\byesterday\b/, () => dayRange(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1), 'yesterday')],
    [/\btoday\b/, () => dayRange(now, 'today')],
    [/\blast week\b/, () => ({
      kind: 'days',
      startDate: isoDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7)),
      endDate: isoDay(now),
      label: 'the last 7 days',
      assumed: false,
    })],
    // Trailing windows measured back from today: "the past 3 days", "the last
    // two weeks", "the past couple of days", "the past month", "2 days ago".
    [new RegExp(`\\b(?:past|last|previous|prior)\\s+(${COUNT_WORD_PATTERN})\\s+days?\\b`), (m) => {
      const n = parseCountWord(m[1]);
      return n && trailingDaysRange(now, n, n === 1 ? 'the past day' : `the past ${n} days`);
    }],
    [new RegExp(`\\b(?:past|last|previous|prior)\\s+(${COUNT_WORD_PATTERN})\\s+weeks?\\b`), (m) => {
      const n = parseCountWord(m[1]);
      return n && n <= 52 && trailingDaysRange(now, n * 7, n === 1 ? 'the past week' : `the past ${n} weeks`);
    }],
    [new RegExp(`\\b(?:past|last|previous|prior)\\s+(${COUNT_WORD_PATTERN})\\s+months?\\b`), (m) => {
      const n = parseCountWord(m[1]);
      return n && n <= 24 && trailingMonthsRange(now, n, n === 1 ? 'the past month' : `the past ${n} months`);
    }],
    [/\b(?:past|previous)\s+week\b/, () => trailingDaysRange(now, 7, 'the past 7 days')],
    [/\b(?:past|previous)\s+month\b/, () => trailingMonthsRange(now, 1, 'the past month')],
    // Week-to-date, weeks starting Sunday. Deliberately generous for a delete
    // preview: the batch is reviewed and confirmed before anything happens.
    [/\bthis week\b/, () => ({
      kind: 'days',
      startDate: isoDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay())),
      endDate: isoDay(now),
      label: 'this week',
      assumed: false,
    })],
    [new RegExp(`\\b(${COUNT_WORD_PATTERN})\\s+days?\\s+ago\\b`), (m) => {
      const n = parseCountWord(m[1]);
      return n && dayRange(new Date(now.getFullYear(), now.getMonth(), now.getDate() - n), `${n} days ago`);
    }],
  ];
  for (const [re, build] of relative) {
    const m = text.match(re);
    if (!m) continue;
    const range = build(m);
    if (range) found.push({ index: m.index, range });
  }

  found.sort((a, b) => a.index - b.index);
  const seen = new Set();
  const ranges = [];
  for (const f of found) {
    const key = `${f.range.startDate}:${f.range.endDate}`;
    if (seen.has(key)) continue;
    seen.add(key);
    ranges.push(f.range);
    if (ranges.length === 2) break;
  }
  // "Compare August to September 2024": one explicit year anchors the pair —
  // an assumed August 2025 next to an explicit September 2024 is never what
  // the user meant.
  if (ranges.length === 2 && ranges[0].kind === 'month' && ranges[1].kind === 'month'
      && ranges[0].assumed !== ranges[1].assumed) {
    const explicitYear = parseInt((ranges[0].assumed ? ranges[1] : ranges[0]).startDate.slice(0, 4), 10);
    const idx = ranges[0].assumed ? 0 : 1;
    const monthIdx = parseInt(ranges[idx].startDate.slice(5, 7), 10) - 1;
    const rebased = monthRange(explicitYear, monthIdx, { assumed: true });
    if (`${rebased.startDate}:${rebased.endDate}` !== `${ranges[1 - idx].startDate}:${ranges[1 - idx].endDate}`) {
      ranges[idx] = rebased;
    }
  }
  return ranges;
};

// ── Intent mapping ────────────────────────────────────────────────────────────
const COMPARE_RE = /\bcompare\b|\bcomparison\b|\bvs\.?\b|\bversus\b|difference between|month over month/;
// Recency anchored on today: "latest/most recent" = newest completed,
// "oldest/earliest" = the oldest we can see. Bare "last" stays out — it's too
// ambiguous ("last Friday", "last completed count" is count_review already).
const LATEST_RE = /\b(?:latest|most recent|newest)\b/;
const OLDEST_RE = /\b(?:oldest|earliest)\b/;
// "compare the last two" = the newest count/report and the one before it.
const LAST_TWO_RE = /\b(?:last|latest|most recent|past|previous)\s+(?:two|2)\b/;
// …but "the past 2 days/weeks/months" is a time WINDOW, not two counts —
// unless the user explicitly says compare ("compare the last two months").
const LAST_TWO_UNIT_RE = /\b(?:last|latest|most recent|past|previous)\s+(?:two|2)\s+(?:days?|weeks?|months?|years?|hours?)\b/;

// Words that never make a useful scope on their own.
const SCOPE_STOPWORDS = new Set([
  'my', 'our', 'the', 'a', 'an', 'all', 'total', 'entire', 'whole', 'full',
  'ending', 'beginning', 'much', 'more', 'less', 'me', 'items', 'item', 'stuff',
  'current', 'complete', 'completed', 'last', 'this', 'that', 'inventory',
  'was', 'were', 'is', 'are', 'have', 'has', 'had', 'of', 'your', 'their',
  'vendor', 'vendors', 'month', 'week', 'year', 'yesterday', 'today',
  ...MONTH_SPELLINGS.flat(),
]);

const cleanScope = (s) => {
  let t = String(s || '').trim().toLowerCase().replace(/[?.!,]+$/, '').trim();
  t = t.replace(/^(?:a|an|the|my|our|some) /, '').trim();
  // Drop trailing filler/time tokens ("walk-in last week" → "walk-in").
  let tokens = t.split(/\s+/);
  while (tokens.length > 1 && SCOPE_STOPWORDS.has(tokens[tokens.length - 1])) tokens = tokens.slice(0, -1);
  t = tokens.join(' ');
  if (!t || SCOPE_STOPWORDS.has(t)) return null;
  if (t.length < 2 || t.length > 40) return null;
  return t;
};

const firstMatch = (text, regexes) => {
  for (const re of regexes) {
    const m = text.match(re);
    if (m) return m;
  }
  return null;
};

// How-to / advice phrasings belong to the support answerer, not the report
// flow: "how is COGS calculated?", "what should my food cost be?", "how do I
// set my beginning inventory?". (Deliberately does NOT match "how much" /
// "how many" — those are data questions.)
const HOW_TO_RE = /^\s*(?:how (?:do|does|did|can|could|should|would|is|are)|where (?:do|does|can|is|are)|why\b|what should|what goes|what counts)/;

// A destructive imperative is never a report question. Without this guard,
// "delete all items in inventory for the past 2 days" read "past 2" as "the
// last two counts" and answered a DELETE command with an inventory valuation.
const DESTRUCTIVE_COMMAND_RE = /\b(?:delete|remove|removing|archive|archiving|get rid of|clear out|clean out|wipe|purge)\b/;

const EOP_RES = [
  /\bcogs\b/,
  /\bcost of goods\b/,
  /\bactual\s+(?:food|beverage|bar|liquor|beer|wine|pour)?\s*cost\b/,
  // "my food cost", "the wine cost for August" — a possessive/article keeps
  // this off item-price questions like "how much does that wine cost?".
  /\b(?:my|our|the|actual)\s+(?:food|beverage|bar|liquor|beer|wine|pour)\s+cost\b/,
  /\b(?:end of period|eop)\s+(?:report|numbers|figures|summary|results?)\b/,
  // No "run"/"get" here: "am I ready to run End of Period?" is a setup-review
  // question and must reach the classifier.
  /\b(?:what|show|see|review|pull up)\b[^]*\bend of period\b/,
];
const PNL_INVENTORY_RE = /\b(?:p\s*&\s*l|p\s+and\s+l|profit and loss)\b[^]*\binventory\b|\binventory\b[^]*\b(?:p\s*&\s*l|p\s+and\s+l|profit and loss)\b/;

const COUNT_REVIEW_RES = [
  /what did (?:i|we) count/,
  /what (?:was|got) counted/,
  /\bcount review\b/,
  /review (?:my|our|the) (?:last |latest |most recent )?count\b/,
  /(?:show|see|open|pull up)(?: me)? (?:my |our |the )?(?:last|latest|most recent) (?:completed )?count\b/,
  /counted (?:in|at) the\b/,
  /why does (?:this|that|an) item look/,
];

const PURCHASE_SPEND_RES = [
  /what did (?:i|we) (?:buy|order|purchase)/,
  /how much did (?:i|we) (?:buy|spend|order|purchase)/,
  /\b(?:spend|spent)\b[^]*\b(?:with|from|at)\b/,
  // Question forms only — a bare "bought/purchased" would hijack commands like
  // "add the items I bought last week to my Bar template" away from their
  // action flows (the classifier catches other retrieval phrasings).
  /\b(?:what|how much|how many)\b[^]*\b(?:bought|purchased)\b/,
  /\bpurchases?\b\s+(?:from|of|for|in|during|last|this)\b/,
  /\bpurchase history\b\s+(?:for|of)\b/,
  /\b(?:wine|beer|liquor|food|produce|dairy|meat|seafood)\s+purchases?\b/,
];
const PURCHASE_BROWSE_RES = [
  /purchase history/, /purchasing history/, /order history/, /purchase orders? report/,
  /review my purchase/, /review my orders/, /my past orders/, /orders i (?:have )?sent/,
  /what have i ordered/, /what i ordered/, /past purchase/, /see my orders/, /see my purchase/,
];

const PRICE_CHANGE_RES = [
  /prices? (?:have |that )?changed/, /what prices changed/, /which prices changed/,
  /item price change/, /price change report/, /price changes/, /how prices changed/,
  /what changed in price/, /see price change/, /review price change/,
  /how much did (?:the )?price of .+ change/,
  /(?:greatest|biggest|largest|highest|lowest) price (?:increase|decrease|change|movement)s?/,
];
const PRICE_COST_DRIVER_RE = /why (?:are|is|did|have|has) (?:my |our |the )?(?:(food|liquor|beer|wine|beverage|bar) )?costs? (?:so )?(?:high|higher|up|increasing|increased|rising)/;
const PRICE_ITEM_SCOPE_RE = /(?:the )?price of ([a-z0-9][a-z0-9 &'’.-]{1,60}?)(?=\s+(?:change|changed|increase|increased|decrease|decreased|go up|go down|this|last|in|during|over|between)\b|[?.!,]|$)/;

const priceQuestionDetails = (text) => {
  const costDriver = text.match(PRICE_COST_DRIVER_RE);
  const item = text.match(PRICE_ITEM_SCOPE_RE);
  const greatest = /\b(?:greatest|biggest|largest|highest|top)\b/.test(text);
  const lowest = /\b(?:lowest|smallest)\b/.test(text);
  const decrease = /\b(?:decreases?|decreased|declines?|declined|drops?|dropped|lower|fell|down)\b/.test(text);
  const increase = /\b(?:increases?|increased|rise|rises|rose|rising|raised|higher|high|up)\b/.test(text);
  return {
    scope: cleanScope((item && item[1]) || (costDriver && costDriver[1])),
    priceScopeKind: item ? 'item' : ((costDriver && costDriver[1]) ? 'category' : null),
    priceDirection: decrease && !increase ? 'decrease' : ((increase || costDriver) ? 'increase' : null),
    priceInsight: costDriver ? 'cost_drivers' : ((greatest || lowest) ? 'movers' : (item ? 'item_change' : null)),
    priceSort: lowest ? 'lowest' : (greatest ? 'greatest' : null),
  };
};

const VALUE_CUE_RE = /\bvalue\b|\bworth\b|\bvaluation\b|\bdollar/;
// `\bcans\b` (plural only) — `cans?` would match the modal verb "can".
const QUANTITY_CUE_RE = /how many\b|\bquantit|\bcounted quantit|\bon hand\b|\bunits\b|\bbottles?\b|\bcases?\b|\bcans\b|\bkegs?\b/;
const INVENTORY_VALUE_RES = [
  /inventory value/, /value of (?:my |our |the )?inventory/, /inventory (?:was |is )?worth/,
  /worth of (?:my |our )?inventory/, /what my inventory is worth/, /inventory valuation/,
  /how much inventory/,
];
// Guarded separately: "how do I set my beginning inventory?" is a how-to.
const ENDING_INVENTORY_RE = /\b(?:beginning|ending)\s+inventory\b/;
const INVENTORY_AMBIGUOUS_RES = [
  /inventory totals?\b/, /count totals?\b/, /total inventory\b/, /inventory report\b/, /report my inventory\b/,
];
const INVENTORY_TOTALS_RES = [
  /counted quantit/, /quantities by location/, /inventory quantit/, /count(?:ed)? totals?\b[^]*\bquantit/,
];
// "how many <unit>? of <thing> …" / "how much <thing> did I have on hand"
const HOW_MANY_RE = /how many\s+(cases?|bottles?|cans?|kegs?|bags?|boxes?|pounds?|lbs|units?|tubs?|each)?\s*(?:of\s+)?([a-z0-9][a-z0-9 '’-]{1,30}?)\s+(?:did|do|does|have|had|were|was|are|is|we|i\b|on hand)/;
const HOW_MUCH_RE = /how much\s+([a-z][a-z '’-]{1,30}?)\s+(?:did|do|does|have|had|was|is|we|i\b|on hand)/;
const INVENTORY_CONTEXT_RE = /\bcount(?:ed|s)?\b|\binventor/;

const VENDOR_SCOPE_RE = /\b(?:from|with)\s+([a-z0-9][a-z0-9&'’. -]{1,40}?)(?=\s+(?:last|this|in|for|during|between|over|since|month|week|year|to|on|at|so|because|about|before|after|versus|vs|compared|yesterday|today)\b|[?.!,]|$)/;
const PURCHASE_ITEM_SCOPE_RES = [
  /\b(wine|beer|liquor|spirits|food|produce|dairy|meat|seafood|paper|chemical)s?\s+purchases?\b/,
  /purchases?(?:\s+history)?\s+(?:for|of)\s+([a-z0-9][a-z0-9 '’-]{1,30}?)(?=\s+(?:in|for|last|this|during|from)\b|[?.!,]|$)/,
  /purchase history\s+for\s+([a-z0-9][a-z0-9 '’-]{1,30}?)(?=\s+(?:in|for|last|this|during|from)\b|[?.!,]|$)/,
];
// A single word right before "inventory" ("food inventory", "wine inventory").
const CATEGORY_BEFORE_INVENTORY_RE = /(?:^|\s)([a-z][a-z'’-]{1,20})\s+inventor/;
const COUNTED_LOCATION_RE = /counted (?:in|at) (?:the )?([a-z0-9][a-z0-9 '’-]{1,30}?)(?=\s+(?:yesterday|today|last|this|on|in|during|for|before|after)\b|[?.!,]|$)/;

/**
 * Map a plain-English question onto a report query. Returns null when the text
 * doesn't look like a report-data question (the caller then falls through to
 * the other flows / the model classifier).
 *
 * Shape: {
 *   report_type: one of REPORT_QUERY_TYPES, or null when only the kind is
 *                unclear (needs_kind_clarification) or fallbackType was absent,
 *   needs_kind_clarification: "inventory total" — dollar value or quantities?
 *   comparison: user asked to compare periods,
 *   ranges: up to two { startDate, endDate, label, assumed } ranges,
 *   scope: item/category/location filter ("wine", "chicken", "bar"), or null,
 *   vendorScope: purchase-history vendor filter ("Sysco"), or null,
 *   purchaseSpend: purchase question is about what was actually bought
 *                  (excludes drafts/canceled), not a browse of all orders,
 * }
 */
export const parseReportQuestion = (raw, { now = new Date(), fallbackType = null } = {}) => {
  const text = String(raw || '').toLowerCase();
  if (!text.trim()) return null;
  // Reports are read-only; a command to destroy data belongs to the delete
  // flows and must fall through to them, never be answered with a report.
  if (DESTRUCTIVE_COMMAND_RE.test(text)) return null;

  const ranges = parseDateRanges(text, now);
  const howTo = HOW_TO_RE.test(text);
  const recency = LATEST_RE.test(text) ? 'latest' : (OLDEST_RE.test(text) ? 'oldest' : null);
  const compareWord = COMPARE_RE.test(text);
  // "compare the last two" / "compare the most recent totals" = newest vs the
  // one before it, no named periods required. A unit word after the number
  // ("the past 2 days") makes it a window instead, unless compare was said.
  const lastTwo = (LAST_TWO_RE.test(text) && (compareWord || !LAST_TWO_UNIT_RE.test(text)))
    || (compareWord && recency === 'latest' && ranges.length === 0);
  // Two month ranges joined by "and" ("inventory value for August and
  // September") are a comparison even without a compare keyword — answering
  // only the first and silently dropping the second would be wrong.
  const comparison = compareWord || lastTwo
    || (ranges.length === 2 && ranges.every((r) => r.kind === 'month'));
  const base = {
    report_type: null,
    needs_kind_clarification: false,
    comparison,
    lastTwo,
    recency,
    ranges,
    scope: null,
    vendorScope: null,
    purchaseSpend: false,
    // Kept so the session lookup can match count/template names the user
    // mentioned ("…for PoP Offsite Dallas", "the gils count").
    sourceText: String(raw || ''),
  };

  // Price changes first — they sit next to the price-update flow and must win.
  if (PRICE_CHANGE_RES.some((re) => re.test(text)) || PRICE_COST_DRIVER_RE.test(text)) {
    return { ...base, report_type: 'price_changes', ...priceQuestionDetails(text) };
  }

  // End of Period / actual COGS. How-to questions ("how is COGS calculated?",
  // "how do I run End of Period?") stay with the classifier → support.
  if (!howTo && (EOP_RES.some((re) => re.test(text)) || PNL_INVENTORY_RE.test(text))) {
    return { ...base, report_type: 'eop_cogs' };
  }

  // Completed count review.
  if (COUNT_REVIEW_RES.some((re) => re.test(text))) {
    const loc = text.match(COUNTED_LOCATION_RE);
    return { ...base, report_type: 'count_review', scope: cleanScope(loc && loc[1]) };
  }

  // Purchase history.
  const spend = PURCHASE_SPEND_RES.some((re) => re.test(text));
  if (spend || PURCHASE_BROWSE_RES.some((re) => re.test(text))) {
    const vendor = text.match(VENDOR_SCOPE_RE);
    const item = firstMatch(text, PURCHASE_ITEM_SCOPE_RES);
    return {
      ...base,
      report_type: 'purchase_history',
      purchaseSpend: spend,
      vendorScope: cleanScope(vendor && vendor[1]),
      scope: cleanScope(item && item[1]),
    };
  }

  // Recency-anchored questions: "what is my latest total?", "compare the most
  // recent totals in dollars", "compare the last two counts". These name no
  // period — the lookup resolves "latest"/"last two" against completed counts.
  if ((recency || lastTwo) && /\btotals?\b|\bvalue\b|\bworth\b|\bcounts?\b|\binventor/.test(text)) {
    if (QUANTITY_CUE_RE.test(text) && !VALUE_CUE_RE.test(text)) {
      return { ...base, report_type: 'inventory_totals' };
    }
    if (VALUE_CUE_RE.test(text) || comparison) {
      // Comparisons are always value-based (quantities span mixed units).
      return { ...base, report_type: 'inventory_value' };
    }
    return { ...base, report_type: null, needs_kind_clarification: true };
  }

  // Counted quantities ("how many bottles of cabernet…", "how much chicken…").
  const howMany = text.match(HOW_MANY_RE);
  const howMuch = !/how much inventory/.test(text) ? text.match(HOW_MUCH_RE) : null;
  const howMuchThing = howMuch ? String(howMuch[1] || '').trim() : '';
  // "how much food inventory did I have" is a VALUE question about food; only a
  // non-inventory thing ("chicken", "wine") is a quantity question.
  const howMuchIsValue = /\binventor/.test(howMuchThing);
  if ((howMany || (howMuch && !howMuchIsValue) || INVENTORY_TOTALS_RES.some((re) => re.test(text)))) {
    // Require inventory/count context (an explicit unit like "cases of" also
    // counts) so "how many vendors do I have?" doesn't land here.
    const contextual = Boolean(howMany && howMany[1])
      || /\bon hand\b|\bstock\b/.test(text)
      || INVENTORY_CONTEXT_RE.test(text);
    if (contextual) {
      return {
        ...base,
        report_type: 'inventory_totals',
        scope: cleanScope((howMany && howMany[2]) || howMuchThing),
      };
    }
  }

  // Dollar value vs the ambiguous "inventory total".
  const valueHit = INVENTORY_VALUE_RES.some((re) => re.test(text))
    || howMuchIsValue
    || (!howTo && ENDING_INVENTORY_RE.test(text));
  const ambiguousHit = INVENTORY_AMBIGUOUS_RES.some((re) => re.test(text));
  const compareInventory = comparison && /\binventor/.test(text);
  if (valueHit || ambiguousHit || compareInventory) {
    const cat = text.match(CATEGORY_BEFORE_INVENTORY_RE);
    const scope = cleanScope((cat && cat[1]) || (howMuchIsValue ? howMuchThing.replace(/\binventor\w*/g, '') : null));
    if (QUANTITY_CUE_RE.test(text) && !valueHit) {
      return { ...base, report_type: 'inventory_totals', scope };
    }
    if (valueHit || VALUE_CUE_RE.test(text) || compareInventory) {
      return { ...base, report_type: 'inventory_value', scope };
    }
    // "inventory total for August" — dollar value or counted quantities?
    return { ...base, report_type: null, needs_kind_clarification: true, scope };
  }

  if (fallbackType && REPORT_QUERY_TYPES.includes(fallbackType)) {
    return { ...base, report_type: fallbackType };
  }
  return null;
};

// ── Session lookup ("report sessions" = completed count sessions) ─────────────
/**
 * List the completed count sessions a report can be built from, newest first.
 * Mirrors the Reports page's eligibility: completed, not voided, not trashed,
 * not a template (archived counts stay in). Order counts are excluded for the
 * financial reports; count_review can look at any completed count.
 */
export const listReportSessionsForAI = async ({ supabase, storeId, reportType = 'inventory_value', range = null, limit = 5 }) => {
  const { data, error } = await supabase
    .from('count_sessions')
    .select('id, name, status, completed_at, session_date, created_at, order_calculations_enabled, count_purpose, is_template, is_trashed, voided_at, template_name_snapshot')
    .eq('store_id', storeId)
    .eq('status', 'completed')
    .order('completed_at', { ascending: false, nullsFirst: false })
    .limit(200);
  if (error) throw error;
  const includeOrderCounts = reportType === 'count_review';
  return (data || [])
    .filter((s) => !s.is_template && s.is_trashed !== true && !s.voided_at)
    // Order counts are split off by order_calculations_enabled ONLY — the same
    // rule as the Reports page picker (count_purpose defaults to 'ordering' on
    // older rows and would wrongly exclude real inventory counts). No
    // items_counted filter either: that column is denormalized and unreliable,
    // and the Reports page includes every completed count regardless.
    .filter((s) => includeOrderCounts || s.order_calculations_enabled !== true)
    .map((s) => ({
      id: s.id,
      name: s.name || `Count ${s.id}`,
      templateName: s.template_name_snapshot || null,
      date: s.completed_at || s.session_date || s.created_at || null,
      // The business day the app displays for the run: session_date wins over
      // the completion timestamp (a "May 31" count is often completed June 1).
      day: (typeof s.session_date === 'string' && s.session_date.slice(0, 10))
        || (s.completed_at ? isoDay(new Date(s.completed_at)) : null),
    }))
    // Date matching happens on the business day, in the user's calendar — not
    // on UTC completion instants, which shove evening month-end counts into
    // the next month.
    .filter((s) => !range || (s.day && s.day >= range.startDate && s.day <= range.endDate))
    .slice(0, limit);
};

// Words too generic to identify a count/template by name.
const NAME_MENTION_STOPWORDS = new Set([
  'count', 'counts', 'counting', 'inventory', 'template', 'templates', 'run', 'runs',
  'weekly', 'monthly', 'daily', 'recurring', 'period', 'test', 'new', 'copy',
  ...MONTH_SPELLINGS.flat(),
]);
const normNameText = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();

/**
 * The counts whose name or template name the user actually mentioned
 * ("…for PoP Offsite Dallas", "the gils count"). Matches the full normalized
 * name as a phrase, or any distinctive (4+ letter, non-generic) name token.
 * Returns [] when nothing was mentioned — callers fall back to the full list.
 */
export const filterSessionsByNameMention = (sessions, text) => {
  const q = ` ${normNameText(text)} `;
  if (!q.trim()) return [];
  return (sessions || []).filter((s) => [s.name, s.templateName].filter(Boolean).some((n) => {
    const nn = normNameText(n);
    if (!nn) return false;
    if (q.includes(` ${nn} `) || q.includes(nn)) return true;
    return nn.split(' ')
      .filter((t) => t.length >= 4 && !NAME_MENTION_STOPWORDS.has(t) && !/^\d+$/.test(t))
      .some((t) => q.includes(t));
  }));
};

/** Count-level lookup for "what did I count …" questions (includes order counts). */
export const findCompletedCountsForAI = (opts) =>
  listReportSessionsForAI({ ...opts, reportType: 'count_review' });

// ── Saved End of Period reports (pnl_reports) ─────────────────────────────────
const formatShortDate = (value) => {
  if (!value) return '';
  const parsed = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/**
 * List saved End of Period reports, newest first, optionally within a period.
 * pnl_reports is the app's ONLY persisted report artifact — actual COGS always
 * comes from these saved rows, never re-derived from raw counts.
 */
export const listEopReportsForAI = async ({ supabase, storeId, range = null, limit = 5 }) => {
  let query = supabase
    .from('pnl_reports')
    .select(`
      id, report_date, period_label, report_type, start_session_id, end_session_id,
      count_group_name, actual_usage_total, aggregate_cogs_pct, net_sales_total,
      report_data, created_at
    `)
    .eq('store_id', storeId)
    .order('report_date', { ascending: false })
    .limit(40);
  if (range?.startDate) query = query.gte('report_date', range.startDate);
  if (range?.endDate) query = query.lte('report_date', range.endDate);
  const { data, error } = await query;
  if (error) throw error;
  return (data || []).slice(0, limit);
};

export const eopReportLabel = (r) => {
  const date = formatShortDate(r?.report_date);
  const name = r?.period_label || 'End of Period';
  return date ? `${name} · ${date}` : name;
};

export const eopReportChips = (reports) =>
  (reports || []).map((r) => ({ key: `eop-${r.id}`, label: eopReportLabel(r) }));

export const resolveEopReportReply = (text, reports) => {
  const list = reports || [];
  if (!list.length) return null;
  const t = String(text || '').trim().toLowerCase();
  if (!t) return null;
  // Exact chip label and name matches BEFORE the "latest" shortcut — a saved
  // report named "First Half 2025" or "Last Week of August" must resolve to
  // itself when its chip is tapped, not to list[0].
  for (const r of list) {
    if (eopReportLabel(r).toLowerCase() === t) return r;
  }
  for (const r of list) {
    const name = String(r.period_label || '').toLowerCase();
    if (name && (t.includes(name) || name.includes(t))) return r;
    const date = formatShortDate(r.report_date).toLowerCase();
    if (date && t.includes(date)) return r;
  }
  if (/\b(latest|most recent|recent|newest|last|first|top)\b/.test(t)) return list[0];
  return null;
};

// ── "Which kind / which report?" clarification ────────────────────────────────
export const reportKindChips = () => [
  { key: 'kind-value', label: 'Dollar value' },
  { key: 'kind-totals', label: 'Counted quantities' },
];

export const resolveReportKindReply = (text) => {
  const t = String(text || '').toLowerCase();
  if (!t.trim()) return null;
  if (/dollar|value|worth|\$|money|financial/.test(t)) return 'inventory_value';
  if (/quantit|counted|count|how many|amount|number|unit/.test(t)) return 'inventory_totals';
  return null;
};

export const reportTypeChips = () => [
  { key: 'rt-value', label: 'Inventory value' },
  { key: 'rt-totals', label: 'Counted quantities' },
  { key: 'rt-purchases', label: 'Purchase history' },
  { key: 'rt-eop', label: 'End of Period / COGS' },
  { key: 'rt-prices', label: 'Price changes' },
];

export const resolveReportTypeReply = (text) => {
  const t = String(text || '').toLowerCase();
  if (!t.trim()) return null;
  if (/price/.test(t)) return 'price_changes';
  if (/purchase|order history|bought|buy/.test(t)) return 'purchase_history';
  if (/cogs|end of period|eop|cost of goods|p\s*&\s*l/.test(t)) return 'eop_cogs';
  if (/review|last count|what.*counted/.test(t)) return 'count_review';
  if (/quantit|counted|totals|how many/.test(t)) return 'inventory_totals';
  if (/value|worth|dollar/.test(t)) return 'inventory_value';
  return null;
};

// ── Missing-report handling ───────────────────────────────────────────────────
/**
 * The "no matching report yet" next step: what is missing, how to run it, and
 * the deep link to get there. Never invents a number when the report is absent.
 */
export const prepareReportRunForAI = ({ reportType, rangeLabel = '' }) => {
  const period = rangeLabel ? ` for ${rangeLabel}` : '';
  if (reportType === 'eop_cogs') {
    return {
      text: `I don’t see a saved End of Period report${period}, so I can’t give you an actual COGS figure yet. Run End of Period under Reports → End of Period — it values your beginning and ending counts, adds purchases, and saves the report — then ask me again.`,
      link: { href: '/reports.html', label: 'Open Reports →' },
    };
  }
  if (reportType === 'count_review') {
    return {
      text: `I don’t see a completed count${period} to review. Complete a count first, then ask me again.`,
      link: { href: '/count.html?view=overview', label: 'Open Counts →' },
    };
  }
  const what = reportType === 'inventory_totals' ? 'report counted quantities' : 'value your inventory';
  return {
    text: `I don’t see a completed inventory count${period}, so I can’t ${what} for it without guessing — and I don’t guess at your numbers. Complete a count${rangeLabel ? ' covering that period' : ''}, then ask me again and I’ll run it from the real data.`,
    link: { href: '/count.html?view=overview', label: 'Open Counts →' },
  };
};
